/**
 * The one shape of a choice option (`custom_fields.options[]`), and the one
 * place that reads it.
 *
 * `custom_fields.options` is an untyped JSON column. Three shapes exist in the
 * data: `string[]` (the admin dialog), `{label, value}[]` and
 * `{label, value, metaKey}[]` (Meta lead-form import). Every reader used to
 * normalize on its own and dropped whatever it did not know — which is why an
 * image on an option could never have survived a round trip.
 *
 * Rules:
 *  - `label` is what people see AND what gets stored on the contact
 *    (see wert-form.ts → matchCustomFieldOption). `value` is what a form
 *    submits; it equals `label` unless an importer set it differently.
 *  - Unknown keys (`metaKey`, later `meta`, …) are carried through untouched.
 *  - A plain option is written back as a plain string, so fields without
 *    images keep the shape every older reader expects.
 *
 * No DB import on purpose — runs in unit tests and on the client.
 */

export interface CustomFieldOption {
  /** Stable identity, independent of the wording. Only present on rich options. */
  id?: string;
  value: string;
  label: string;
  subtitle?: string;
  imageUrl?: string;
  /** Emoji shown when there is no image. */
  icon?: string;
  /** Anything else an importer stored on the option (e.g. `metaKey`). */
  [extra: string]: unknown;
}

const asTrimmed = (v: unknown): string => (v == null ? '' : String(v).trim());

/**
 * Reads one raw option. Always returns a non-empty string `value` — radix
 * Select crashes on duplicate/empty values ("two children with the same key").
 */
export function normalizeCustomFieldOption(raw: unknown, index = 0): CustomFieldOption {
  if (raw == null) return { value: `opt-${index}`, label: '' };
  if (typeof raw === 'string' || typeof raw === 'number') {
    const s = String(raw);
    return { value: s, label: s };
  }
  if (typeof raw !== 'object') return { value: `opt-${index}`, label: '' };

  const o = raw as Record<string, unknown>;
  const value =
    asTrimmed(o.value) || asTrimmed(o.key) || asTrimmed(o.label) || asTrimmed(o.name) || asTrimmed(o.id) || `opt-${index}`;
  const label = asTrimmed(o.label) || asTrimmed(o.name) || asTrimmed(o.text) || asTrimmed(o.value) || value;

  const option: CustomFieldOption = { ...o, value, label };
  // Older writers used other spellings; read them once, write the canonical key.
  const imageUrl = asTrimmed(o.imageUrl) || asTrimmed(o.image_url) || asTrimmed(o.image);
  const subtitle = asTrimmed(o.subtitle) || asTrimmed(o.description);
  const icon = asTrimmed(o.icon) || asTrimmed(o.emoji);
  delete option.image_url;
  delete option.image;
  delete option.description;
  delete option.emoji;
  if (imageUrl) option.imageUrl = imageUrl; else delete option.imageUrl;
  if (subtitle) option.subtitle = subtitle; else delete option.subtitle;
  if (icon) option.icon = icon; else delete option.icon;
  return option;
}

export function normalizeCustomFieldOptions(raw: unknown): CustomFieldOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((o, i) => normalizeCustomFieldOption(o, i)).filter((o) => o.label !== '');
}

/** True when the option carries anything a plain string could not. */
export function isRichCustomFieldOption(option: CustomFieldOption): boolean {
  const { value, label, id: _id, ...rest } = option;
  if (value !== label) return true;
  return Object.values(rest).some((v) => v != null && v !== '');
}

/** True when at least one option has an image or an icon — cards make sense. */
export function hasVisualCustomFieldOptions(raw: unknown): boolean {
  return normalizeCustomFieldOptions(raw).some((o) => !!o.imageUrl || !!o.icon);
}

/**
 * Options for a field created from a Meta lead-form question. The label is
 * what Meta shows; Meta's internal key rides along as `metaKey`, so an
 * incoming lead matches whichever half Meta sends (wert-form.ts).
 */
export function customFieldOptionsFromMeta(
  options: Array<{ key?: unknown; value?: unknown }> | undefined,
): CustomFieldOption[] {
  return (options ?? [])
    .map((o) => {
      const label = asTrimmed(o?.value ?? o?.key);
      const metaKey = asTrimmed(o?.key);
      return { value: label, label, ...(metaKey && metaKey !== label ? { metaKey } : {}) };
    })
    .filter((o) => o.value !== '');
}

/**
 * The shape that goes back into `custom_fields.options`: plain options as
 * strings, rich ones as objects with a stable id.
 */
export function serializeCustomFieldOptions(
  options: CustomFieldOption[],
  createId: () => string,
): Array<string | CustomFieldOption> {
  return options
    .filter((o) => o.label.trim() !== '')
    .map((o) => {
      if (!isRichCustomFieldOption(o)) return o.label;
      const cleaned: CustomFieldOption = { ...o, id: o.id || createId() };
      for (const key of Object.keys(cleaned)) {
        if (cleaned[key] == null || cleaned[key] === '') delete cleaned[key];
      }
      return cleaned;
    });
}

/**
 * Lays a new option list over the stored one: an option with the same label
 * (case-insensitive) keeps its id and whatever an importer attached — without
 * `metaKey` a Meta lead would no longer hit it. Stored answers are the label,
 * so a renamed option still orphans them.
 */
export function mergeCustomFieldOptions(existing: unknown, next: unknown[]): CustomFieldOption[] {
  const old = normalizeCustomFieldOptions(existing);
  return normalizeCustomFieldOptions(next).map((o) => {
    const prev = old.find((p) => p.label.trim().toLowerCase() === o.label.trim().toLowerCase());
    // Label and value stay as stored: answers hold exactly that spelling.
    return prev ? { ...prev, ...o, value: prev.value, label: prev.label, id: prev.id } : o;
  });
}
